import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router';
import { EmptyState, Skeleton, cn, useI18n } from '@xdocs/ui';
import { useAuth } from '../../auth/AuthProvider.js';
import { PublicShareReader, useViewerRole } from '../../features/sharing/index.js';
import { useDiscoverShare } from './useDiscoverShare.js';
import { useDiscoverT } from './useDiscoverT.js';
import styles from './DiscoverSharePage.module.css';

/**
 * `/discover/:shareId` (F62) — public read view, accessible WITHOUT auth
 * (F62/F63 AC). Deliberately does NOT import `RequireAuth` — this dir's
 * `WIRING.md` documents that the app router must mount this route OUTSIDE
 * `<RequireAuth>` for that to actually reach an anonymous visitor.
 */
export function DiscoverSharePage(): ReactElement {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const { t: tGlobal } = useI18n();
  const { t } = useDiscoverT();
  const { userId } = useAuth();
  const viewerRole = useViewerRole();
  const { view, isLoading, isError, error, refetch } = useDiscoverShare(shareId ?? '');

  if (!shareId) {
    return <EmptyState kind="error" title={t('discover:detail.notFound.title')} />;
  }

  if (isLoading) {
    return (
      <div className={cn(styles['page'])} aria-busy="true">
        <Skeleton shape="text-line" width="40%" />
        <Skeleton shape="card" count={1} height="200px" />
        <Skeleton shape="text-line" count={3} />
      </div>
    );
  }

  if (isError) {
    if (error?.code === 'NOT_FOUND') {
      return (
        <EmptyState
          kind="error"
          title={t('discover:detail.notFound.title')}
          description={t('discover:detail.notFound.description')}
          action={{ label: t('discover:detail.notFound.cta'), onClick: () => void navigate('/discover') }}
          mascotAlt=""
        />
      );
    }
    return <EmptyState kind="error" title={t('discover:detail.error.title')} action={{ label: tGlobal('common:action.retry'), onClick: refetch }} />;
  }

  if (!view) {
    return <EmptyState kind="error" title={t('discover:detail.notFound.title')} />;
  }

  return (
    <PublicShareReader
      view={view}
      viewerUserId={userId}
      viewerRole={viewerRole}
      backTo={{ label: t('discover:page.title'), to: '/discover' }}
      fullReaderTo={`/read/${view.documentId}`}
      onTakenDown={() => void navigate('/discover')}
    />
  );
}
